import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { SEO } from '@/components/layout/SEO'
import { Btn, SectionLabel } from '@/components/ui'
import { C } from '@/constants/theme'
import { PRODUCTS, MAKES, CATS, WA_URL } from '@/constants/data'
import { useCart } from '@/hooks/useCart'
import type { Product } from '@/types'

const CAT_COLORS: Record<string, string> = {
  Windscreen:   C.blue,
  'Rear Screen': '#33C1F5',
  'Door Glass':  '#87CEEB',
}

function ProductCard({ product, onAdd }: { product: Product; onAdd: (p: Product) => void }) {
  const [hov, setHov] = useState(false)
  const [added, setAdded] = useState(false)

  const handleAdd = () => {
    onAdd(product)
    setAdded(true)
    setTimeout(() => setAdded(false), 1500)
  }

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.white,
        border: `1px solid ${hov ? C.blue : C.grayBorder}`,
        transition: 'all 0.25s',
        transform: hov ? 'translateY(-4px)' : 'none',
        boxShadow: hov ? '0 8px 28px rgba(0,174,239,0.1)' : '0 1px 4px rgba(0,0,0,0.04)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Product image */}
      <div style={{ height: 150, background: 'linear-gradient(135deg, #F0F9FF, #E8F4FC)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        {product.image ? (
          <img
            src={product.image}
            alt={`${product.make} ${product.model} ${product.cat}`}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex' }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : null}
        {/* SVG fallback — shown when image is absent or fails to load */}
        <svg
          width="80" height="50" viewBox="0 0 120 72" fill="none" opacity="0.5"
          style={{ display: product.image ? 'none' : 'block', position: product.image ? 'absolute' : 'static' }}
        >
          <ellipse cx="42" cy="36" rx="38" ry="28" stroke="#aaa" strokeWidth="2" fill="white" />
          <path d="M20 28 Q42 8 80 20 Q100 24 108 36" stroke="#aaa" strokeWidth="2" fill="none" />
          <ellipse cx="52" cy="28" rx="16" ry="10" fill={C.blue} opacity="0.35" />
        </svg>
        <div style={{ position: 'absolute', top: 8, left: 8, background: CAT_COLORS[product.cat] || C.blue, color: C.white, fontSize: 9, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: 1, padding: '2px 8px' }}>
          {product.cat.toUpperCase()}
        </div>
        {product.sensor && (
          <div style={{ position: 'absolute', top: 8, right: 8, background: C.white, color: C.blue, fontSize: 9, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: 1, padding: '2px 8px', border: `1px solid ${C.blue}` }}>
            SENSOR
          </div>
        )}
      </div>

      <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 3 }}>
          {product.make} {product.model}
        </div>
        <div style={{ fontSize: 12, color: C.textLight, marginBottom: 14 }}>{product.years}</div>
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 22, fontWeight: 700, color: C.blue }}>
            R{product.price.toLocaleString()}
          </div>
          <button
            onClick={handleAdd}
            style={{
              padding: '6px 14px',
              background: added ? '#22c55e' : C.blue,
              color: C.white,
              fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11,
              letterSpacing: 1, border: 'none', cursor: 'pointer',
              transition: 'background 0.3s', textTransform: 'uppercase',
            }}
          >
            {added ? '✓ Added' : '+ Add'}
          </button>
        </div>
      </div>
    </div>
  )
}

function CartPanel({ cart, onRemove, onClose, onCheckout }: { cart: ReturnType<typeof useCart>; onRemove: (id: string) => void; onClose: () => void; onCheckout: () => void }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.grayBorder}`, borderTop: `3px solid ${C.blue}`, padding: 24, marginBottom: 28, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: C.text }}>Your Cart</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textLight, fontSize: 18 }}>✕</button>
      </div>
      {cart.items.map(item => (
        <div key={item.cartId} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.grayMid}` }}>
          <span style={{ color: C.textMid, fontSize: 14 }}>{item.make} {item.model} ({item.years})</span>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>R{item.price.toLocaleString()}</span>
            <button onClick={() => onRemove(item.cartId)} style={{ background: 'none', border: 'none', color: C.textLight, cursor: 'pointer', fontSize: 12 }}>✕</button>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 22, fontWeight: 700, color: C.blue }}>
          Total: R{cart.total.toLocaleString()}
        </div>
        <Btn onClick={onCheckout}>Proceed to Checkout</Btn>
      </div>
    </div>
  )
}

const PAGE_SIZE = 12

export default function Products() {
  const navigate = useNavigate()
  const [cat, setCat] = useState('All')
  const [make, setMake] = useState('All')
  const [search, setSearch] = useState('')
  const [searchMode, setSearchMode] = useState<'model' | 'reg'>('model')
  const [reg, setReg] = useState('')
  const [cartOpen, setCartOpen] = useState(false)
  const [page, setPage] = useState(1)
  const cart = useCart()

  const filtered = PRODUCTS.filter(p =>
    (cat === 'All' || p.cat === cat) &&
    (make === 'All' || p.make === make) &&
    (search === '' || `${p.make} ${p.model}`.toLowerCase().includes(search.toLowerCase()))
  )

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [cat, make, search])

  const handleAdd = (p: Product) => {
    cart.addItem(p)
    setCartOpen(true)
  }

  const handleCheckout = () => {
    navigate('/checkout', { state: { items: cart.items, total: cart.total } })
  }

  const handleRegSearch = () => {
    const trimmed = reg.trim()
    if (!trimmed) return
    window.open(`https://wa.me/27812529679?text=${encodeURIComponent(`Hi GlassTech! I'd like a quote for registration/VIN: ${trimmed}`)}`, '_blank')
  }

  return (
    <>
      <SEO
        title="Products — Windscreens & Auto Glass"
        description="Browse our full range of SABS-approved windscreens, rear screens, and door glass. Filter by make, model, and glass type. Fast delivery and fitting across Gauteng."
        path="/products"
      />

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #F0F9FF, #F5F7FA)', padding: '60px 24px', borderBottom: `1px solid ${C.grayBorder}`, paddingTop: 'calc(60px + 68px)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <SectionLabel text="Browse & Order" />
            <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 700, color: C.text, margin: 0 }}>Products</h1>
          </div>
          <button
            onClick={() => setCartOpen(o => !o)}
            style={{ padding: '10px 20px', background: cart.items.length > 0 ? C.blue : C.white, color: cart.items.length > 0 ? C.white : C.text, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 1, border: `1px solid ${cart.items.length > 0 ? C.blue : C.grayBorder}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            🛒 Cart ({cart.items.length})
          </button>
        </div>
      </div>

      <div style={{ background: C.grayLight, minHeight: '70vh' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
          {/* Cart panel */}
          {cartOpen && cart.items.length > 0 && (
            <CartPanel cart={cart} onRemove={cart.removeItem} onClose={() => setCartOpen(false)} onCheckout={handleCheckout} />
          )}

          {/* Can't find notice */}
          {cartOpen && cart.items.length === 0 && (
            <div style={{ background: C.white, border: `1px solid ${C.grayBorder}`, padding: '16px 24px', marginBottom: 24, color: C.textMid, fontSize: 14 }}>
              Your cart is empty.
            </div>
          )}

          {/* Filters */}
          <div style={{ background: C.white, padding: '14px 18px', border: `1px solid ${C.grayBorder}`, marginBottom: 24, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search mode toggle + input */}
            <div style={{ flex: '1 1 220px', display: 'flex', gap: 0, alignItems: 'stretch' }}>
              {/* Mode tabs */}
              {(['model', 'reg'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setSearchMode(mode)}
                  style={{
                    padding: '8px 12px', border: `1px solid ${C.grayBorder}`,
                    borderRight: mode === 'model' ? 'none' : undefined,
                    background: searchMode === mode ? C.blue : C.grayLight,
                    color: searchMode === mode ? C.white : C.textMid,
                    fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 10,
                    letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {mode === 'model' ? 'MAKE / MODEL' : 'REG / VIN'}
                </button>
              ))}
              {searchMode === 'model' ? (
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search make or model..."
                  style={{ flex: 1, padding: '8px 14px', background: C.grayLight, border: `1px solid ${C.grayBorder}`, borderLeft: 'none', color: C.text, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, outline: 'none' }}
                />
              ) : (
                <div style={{ flex: 1, display: 'flex', gap: 0 }}>
                  <input
                    value={reg}
                    onChange={e => setReg(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleRegSearch()}
                    placeholder="e.g. ABC 123 GP or VIN..."
                    style={{ flex: 1, padding: '8px 14px', background: C.grayLight, border: `1px solid ${C.grayBorder}`, borderLeft: 'none', color: C.text, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, outline: 'none' }}
                  />
                  <button
                    onClick={handleRegSearch}
                    style={{ padding: '8px 14px', background: C.blue, color: C.white, border: 'none', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: 'pointer' }}
                  >
                    FIND
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CATS.map(c => (
                <button key={c} onClick={() => setCat(c)} style={{ padding: '7px 14px', background: cat === c ? C.blue : 'transparent', color: cat === c ? C.white : C.textMid, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, letterSpacing: 1, border: `1px solid ${cat === c ? C.blue : C.grayBorder}`, cursor: 'pointer' }}>
                  {c}
                </button>
              ))}
            </div>
            <select value={make} onChange={e => setMake(e.target.value)} style={{ padding: '7px 14px', background: C.white, color: C.text, border: `1px solid ${C.grayBorder}`, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, cursor: 'pointer', outline: 'none' }}>
              {MAKES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div style={{ fontSize: 11, color: C.textLight, marginBottom: 16, fontFamily: "'Rajdhani', sans-serif", letterSpacing: 1 }}>
            {filtered.length} PRODUCT{filtered.length !== 1 ? 'S' : ''} FOUND
            {totalPages > 1 && ` — PAGE ${page} OF ${totalPages}`}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16 }}>
            {paginated.map(p => <ProductCard key={p.id} product={p} onAdd={handleAdd} />)}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 32 }}>
              {/* Prev */}
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '7px 14px', background: C.white, color: page === 1 ? C.textLight : C.text, border: `1px solid ${C.grayBorder}`, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: 1, cursor: page === 1 ? 'default' : 'pointer' }}
              >
                ← PREV
              </button>

              {/* Page numbers with ellipsis */}
              {(() => {
                const pages: (number | '...')[] = []
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i)
                } else {
                  pages.push(1)
                  if (page > 3) pages.push('...')
                  for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i)
                  if (page < totalPages - 2) pages.push('...')
                  pages.push(totalPages)
                }
                return pages.map((n, i) =>
                  n === '...'
                    ? <span key={`e${i}`} style={{ width: 32, textAlign: 'center', color: C.textLight, fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }}>…</span>
                    : <button key={n} onClick={() => setPage(n)} style={{ width: 36, height: 36, background: n === page ? C.blue : C.white, color: n === page ? C.white : C.textMid, border: `1px solid ${n === page ? C.blue : C.grayBorder}`, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{n}</button>
                )
              })()}

              {/* Next */}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: '7px 14px', background: C.white, color: page === totalPages ? C.textLight : C.text, border: `1px solid ${C.grayBorder}`, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: 1, cursor: page === totalPages ? 'default' : 'pointer' }}
              >
                NEXT →
              </button>
            </div>
          )}

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 24px', background: C.white, border: `1px solid ${C.grayBorder}` }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 8 }}>No results found</div>
              <div style={{ color: C.textMid, fontSize: 14 }}>
                Try adjusting your filters or{' '}
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>request a custom quote</a>.
              </div>
            </div>
          )}

          {/* Can't find CTA */}
          <div style={{ marginTop: 40, background: C.blueLight, border: `1px solid rgba(0,174,239,0.2)`, padding: '28px 36px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 11, letterSpacing: 3, color: C.blue, textTransform: 'uppercase', marginBottom: 4 }}>Can't find your glass?</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 700, color: C.text }}>Request a custom quote and we'll source it for you.</div>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}><Btn>Request Custom Quote</Btn></a>
          </div>
        </div>
      </div>
    </>
  )
}
